import {
  PeerMediaPolicyLifecycleManager,
  NO_MUTUAL_HARDWARE_VIDEO_CODEC,
  ReceiverLatencyHintController,
  WebRtcOptimizationManager,
  getGuestDecodeCapability,
  getGuestDecodeReadiness,
  primeGuestDecodeReadiness,
  type PeerMediaPolicyLifecycle,
} from '@playcastdotio/PeerCore';
import type { PeerMetadata } from '@playcastdotio/Messaging';

/** Feature-level owner for the one media-policy lifecycle used by a guest peer session. */
export class GuestWebRtcPolicyManager {
  public readonly lifecycle: PeerMediaPolicyLifecycle;
  private readiness: Promise<void> | null = null;

  constructor(
    public readonly optimizationManager = new WebRtcOptimizationManager(),
    receiverPolicy = new ReceiverLatencyHintController(),
    createSessionId?: () => string,
  ) {
    this.lifecycle = new PeerMediaPolicyLifecycleManager({
      optimizationManager: this.optimizationManager,
      receiverPolicy,
      ...(createSessionId ? { createSessionId } : {}),
    });
  }

  /** Advertise the completed hardware result; callers must await initialize first. */
  public advertise(metadata: PeerMetadata): PeerMetadata {
    // Only a TERMINAL empty answer (the probe answered "no hardware family") is a refusal.
    // An unanswered probe omits `hardwareDecodeFamilies` and lets the session proceed: the
    // host reads the absence as unknown and applies its own fail-closed policy. Throwing
    // here for an unanswered probe is what turned a slow/absent probe into a guest that
    // could not join at all.
    const readiness = getGuestDecodeReadiness();
    if (readiness?.kind === 'terminal-empty') {
      throw new Error(NO_MUTUAL_HARDWARE_VIDEO_CODEC);
    }
    const hardwareDecodeFamilies = getGuestDecodeCapability();
    if (!hardwareDecodeFamilies?.length) {
      return { ...metadata, signalingGenerationVersion: 1 };
    }
    return {
      ...metadata,
      signalingGenerationVersion: 1,
      hardwareDecodeFamilies,
    };
  }

  /**
   * One bounded, shared readiness barrier used by every join mode.
   *
   * Only a TERMINAL empty answer rejects. An unanswered probe resolves so the join proceeds
   * with unknown capability instead of failing forever, and the rejection is NOT memoized:
   * a later attempt re-probes rather than inheriting the first attempt's verdict.
   */
  public initialize(): Promise<void> {
    this.readiness ??= primeGuestDecodeReadiness().then((readiness) => {
      if (readiness.kind === 'terminal-empty') throw new Error(NO_MUTUAL_HARDWARE_VIDEO_CODEC);
    }).catch((error) => {
      this.readiness = null;
      throw error;
    });
    return this.readiness;
  }
}

